import React, { useState, useEffect } from 'react';
import FormField from '../common/FormField';
import Button from '../common/Button';
import { InvoiceItemDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';

interface InvoiceItemFormProps {
  initialValues: InvoiceItemDTO;
  onSubmit: (data: InvoiceItemDTO) => void;
  onCancel: () => void;
}

const InvoiceItemForm: React.FC<InvoiceItemFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<InvoiceItemDTO>(initialValues);

  const validationRules: ValidationRules = {
    description: [{ required: true, message: 'Description is required' }],
    quantity: [
      { required: true, message: 'Quantity is required' },
      {
        custom: (value) => Number(value) > 0,
        message: 'Quantity must be greater than 0',
      },
    ],
    rate: [
      { required: true, message: 'Rate is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Rate must be a positive number',
      },
    ],
  };

  const { errors, validate } = useFormValidation<InvoiceItemDTO>(validationRules);

  useEffect(() => {
    const quantity = Number(formData.quantity) || 0;
    const rate = Number(formData.rate) || 0;
    setFormData(prev => ({
      ...prev,
      amount: quantity * rate
    }));
  }, [formData.quantity, formData.rate]);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  return (
    <div className="space-y-4">
      <FormField
        id="description"
        label="Description"
        type="text"
        value={formData.description || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Description"
        required={true}
        error={errors.description}
      />
      <FormField
        id="quantity"
        label="Quantity"
        type="number"
        value={formData.quantity || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Quantity"
        required={true}
        error={errors.quantity}
      />
      <FormField
        id="rate"
        label="Rate"
        type="number"
        value={formData.rate || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Rate"
        required={true}
        error={errors.rate}
      />
      <FormField
        id="amount"
        label="Amount"
        type="number"
        value={formData.amount || '0'}
        onChange={(e) => handleChange(e, setFormData)}
        disabled={true}
        placeholder="Calculated Amount"
      />
      
      <div className="flex justify-end space-x-2 pt-4">
        <Button
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default InvoiceItemForm;